/* KinkKart nav v2 (2026-10-10): desktop menu disclosures (snippets/kk-header-nav).
   - Mouse: hovering a top-level item opens its panel (short delay so a pointer
     passing through doesn't flash panels), leaving closes it after a beat so
     the pointer can cross the gap into the panel.
   - Keyboard / touch: the caret button toggles; Escape closes and returns focus;
     tabbing out of an item closes it.
   - One panel at a time; clicks outside and scrolling away close it.
   - Panel images are lazy; on desktop they're switched to eager once the page
     is idle (or as soon as the pointer reaches the nav), so a panel opens with
     its photos already there. Phones never load them unless the drawer opens. */

class KkNavItem extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.kk-nav__toggle');
    this.panel = this.querySelector('.kk-nav__panel');
    if (!this.toggle || !this.panel) return;

    this.toggle.addEventListener('click', () => {
      if (this.isOpen && this.openedByHover) {
        this.openedByHover = false;
        return;
      }
      this.isOpen ? this.close() : this.open();
    });
    this.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.timer);
      const delay = KkNavItem.openItem() ? 0 : 90;
      this.timer = setTimeout(() => {
        if (!this.isOpen) this.openedByHover = true;
        this.open();
      }, delay);
    });
    this.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.close(), 220);
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.isOpen) return;
      this.close();
      this.toggle.focus();
    });
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  static items() {
    return document.querySelectorAll('kk-nav-item');
  }

  static openItem() {
    return [...KkNavItem.items()].find((item) => item.isOpen);
  }

  open() {
    if (this.isOpen) return;
    KkNavItem.items().forEach((item) => item !== this && item.close());
    this.panel.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
    this.openedAt = window.scrollY;
    document.documentElement.classList.toggle('kk-mega-open', this.panel.classList.contains('kk-mega'));
  }

  close() {
    clearTimeout(this.timer);
    this.openedByHover = false;
    if (!this.isOpen) return;
    this.panel.hidden = true;
    this.toggle.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
    if (!KkNavItem.openItem()) document.documentElement.classList.remove('kk-mega-open');
  }
}

customElements.define('kk-nav-item', KkNavItem);

document.addEventListener('click', (event) => {
  if (event.target.closest('kk-nav-item')) return;
  KkNavItem.items().forEach((item) => item.close());
});

window.addEventListener(
  'scroll',
  () => {
    const open = KkNavItem.openItem();
    if (open && Math.abs(window.scrollY - open.openedAt) > 80) open.close();
  },
  { passive: true }
);

function kkNavPreloadImages() {
  document.querySelectorAll('.kk-nav__panel img[loading="lazy"]').forEach((img) => {
    img.loading = 'eager';
  });
}
document.querySelectorAll('.kk-nav__list').forEach((list) => {
  list.addEventListener('pointerenter', kkNavPreloadImages, { once: true });
});
if (window.matchMedia('(min-width: 990px)').matches) {
  window.addEventListener('load', () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(kkNavPreloadImages, { timeout: 3000 });
    else setTimeout(kkNavPreloadImages, 1500);
  });
}
